import { useMemo } from "react";
import axios from "axios";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Suborder } from "$types";

const useEDBIntegratedOrdersList = (suborders: Suborder[]) => {
  // Keep key stable even if parent recreates suborder objects.
  const suborderIds = useMemo(
    () =>
      (suborders || [])
        .map((s) => s.id)
        .filter(Boolean)
        .slice()
        .sort(),
    [suborders]
  );

  const baseUrl = process.env.REACT_APP_API_ENDPOINT || "http://localhost:8083";

  const { data: edbsIntegratedOrders = [] } = useQuery({
    queryKey: ["edbsBySuborderIds", suborderIds.join(",")],

    queryFn: async () => {
      const params = new URLSearchParams();

      suborderIds.forEach((id, index) => {
        params.append(
          `filters[orderProducts][suborderId][$in][${index}]`,
          id.toString()
        );
      });

      params.append("pagination[page]", "1");
      params.append("pagination[pageSize]", "99999");
      params.append("populate[orderProducts][populate]", "*");

      const url = `${baseUrl}/api/order/edbs?${params.toString()}`;

      try {
        const response = await axios.get(url);
        return response.data?.data || response.data || [];
      } catch (err: any) {
        // When no EDB exists, backend can respond 404; treat as empty (and no retries).
        if (err?.response?.status === 404) return [];
        throw err;
      }
    },

    enabled: suborderIds.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });

  return {
    edbsIntegratedOrders,
  };
};

export default useEDBIntegratedOrdersList;
